import { useMemo, useCallback } from 'react';

import { useFormContext } from 'react-hook-form';
import Asterisk from '@decisiv/iconix/lib/components/Asterisk';
import { Flex, Message } from '@decisiv/ui-components/lib/components';
import Grid from '@decisiv/ui-components/lib/components/Grid';
import { TextField, Combobox, TextArea } from 'components/Form';

import { getStatesByCountry } from 'utils/countryRegions';
import getInputEventValue from 'utils/getInputEventValue';

import { Trans, t } from '@lingui/macro';

import { COUNTRY_OPTIONS } from 'pages/Management/constants';
import fleetAdminDeniedReasonText from 'utils/User/fleetAdminDeniedReason';
import { FormContainer } from './styles';
import { useManagedPlatformAccountsQuery } from './hooks';

const CLEAR_STATE_SELECT = t`Clear State`;

type AccountOption = { id: string; label: string; value: string };

interface DepotFormProps {
  accountId?: string;
  accountsQuery: ReturnType<typeof useManagedPlatformAccountsQuery>;
  capability: FleetAdminCapability | null;
  onAccountSearchChange: (value: string) => void;
}

export default function DepotForm({
  accountId,
  accountsQuery,
  capability,
  onAccountSearchChange,
}: DepotFormProps) {
  const { watch, setValue } = useFormContext();
  const countryValue = watch('country');

  const { data: accounts, loading: loadingAccounts } = accountsQuery;

  const accountsOptions: AccountOption[] = useMemo(() => {
    if (!accounts?.managedPlatformAccounts?.collection) return [];

    const formattedData = accounts.managedPlatformAccounts.collection.map(
      (account) => ({
        id: account.id,
        label: account.name,
        value: account.id,
      }),
    );

    return formattedData;
  }, [accounts]);

  const stateOptions = useMemo(
    () => (countryValue ? getStatesByCountry(countryValue) : []),
    [countryValue],
  );

  const handleOnCountryChange = useCallback(() => {
    setValue('state', '');
  }, [setValue]);

  const isDenied = capability?.allowed === false;

  return (
    <FormContainer>
      <Grid.Row>
        <Grid.Column>
          <Flex justifyContent="flex-end">
            <Message color="information" icon={Asterisk}>
              <Trans>Required Fields</Trans>
            </Message>
          </Flex>
        </Grid.Column>
      </Grid.Row>
      {!accountId && (
        <Grid.Row>
          <Combobox
            label={t`Account`}
            name="accountId"
            options={accountsOptions}
            variant="async"
            loading={loadingAccounts}
            onChangeInputValue={(e) =>
              onAccountSearchChange(getInputEventValue(e))
            }
            defaultValue=""
            required
          />
        </Grid.Row>
      )}
      {isDenied && capability && (
        <Grid.Row>
          <Grid.Column>
            <Message intent="warning">
              {fleetAdminDeniedReasonText(capability.deniedReason)}
            </Message>
          </Grid.Column>
        </Grid.Row>
      )}
      <Grid.Row>
        <TextField label={t`Name`} name="name" required disabled={isDenied} />
      </Grid.Row>
      <Grid.Row>
        <TextField label={t`Email`} name="email" required disabled={isDenied} />
      </Grid.Row>
      <Grid.Row>
        <TextField
          label={t`FMS ID`}
          name="dmsId"
          maxLength={40}
          helpMessage={t`Max 40 Characters (Alphanumeric only)`}
          disabled={isDenied}
        />
      </Grid.Row>
      <Grid.Row>
        <TextField label={t`Address`} name="address" disabled={isDenied} />
      </Grid.Row>
      <Grid.Row>
        <Grid.Column marginLeft={0} paddingLeft={0} paddingRight={0.5}>
          <Combobox
            label={t`Country`}
            name="country"
            options={COUNTRY_OPTIONS}
            onChange={handleOnCountryChange}
            defaultValue=""
            required
            disabled={isDenied}
            variant="sync"
          />
        </Grid.Column>
        <Grid.Column marginRight={0} paddingRight={0} paddingLeft={0.5}>
          <Combobox
            clearButtonLabel={CLEAR_STATE_SELECT}
            label={t`State`}
            name="state"
            options={stateOptions}
            defaultValue=""
            required
            disabled={isDenied}
            variant="sync"
          />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column marginLeft={0} paddingLeft={0} paddingRight={0.5}>
          <TextField label={t`City`} name="city" required disabled={isDenied} />
        </Grid.Column>
        <Grid.Column marginRight={0} paddingRight={0} paddingLeft={0.5}>
          <TextField
            label={t`Postal Code`}
            name="postalCode"
            disabled={isDenied}
          />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <TextArea
          label={t`Notes`}
          name="note"
          maxLength={1000}
          helpMessage={t`You have ${
            1000 - (watch('note')?.length || 0)
          } characters left (1000 max).`}
          disabled={isDenied}
        />
      </Grid.Row>
    </FormContainer>
  );
}
